import AuthMethodSwitch from '@components/AuthMethods/AuthMethodSwitch';
import { ActionsFooter, ApprovalDialogButton } from '@components/Forms';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import HelpTooltip from '@components/Tooltips/HelpTooltip';
import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material';
import { onSubmitForm } from '@utils/forms';
import { urlValidationScheme } from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React, { FC } from 'react';
import { IOIDCSettings } from 'types/settings';
import { object } from 'yup';

interface IOIDCProps {
	settings?: IOIDCSettings;
	onSave: (data: IOIDCSettings) => Promise<IOIDCSettings>;
}

const DEFAULT_FORM: IOIDCSettings = {
	id: '',
	version: 0,
	enabled: false,
	redirectURL: ''
};

const OIDCSettings: FC<IOIDCProps> = ({ onSave, settings }) => {
	const { addToast } = useToast();

	const formik = useFormik<IOIDCSettings>({
		enableReinitialize: true,
		validationSchema: object({
			redirectURL: urlValidationScheme()
		}),
		initialValues: {
			...DEFAULT_FORM,
			...settings
		},
		onSubmit: onSubmitForm<IOIDCSettings>(onSave, addToast)
	});

	return (
		<Box
			display="flex"
			flexDirection="column"
			flexGrow={1}
			overflow="hidden"
			data-testid="authentication-methods-oidc"
		>
			<Box
				display="flex"
				flexDirection="column"
				flexGrow={1}
				component="form"
				pl={4}
			>
				<Grid item>
					<AuthMethodSwitch formik={formik} />
				</Grid>
				<Grid
					container
					flexGrow={1}
					spacing={2}
					mt={1}
					flexDirection="row"
					alignItems="flex-start"
				>
					<Grid item xs={8}>
						<TextFieldValidation
							id="redirectURL"
							formik={formik}
							autoFocus
							label={intl.tr(
								'pages.authMethods.sso.oidc.input.redirecturl.title'
							)}
							fullWidth
							placeholder={intl.tr(
								'pages.authMethods.sso.oidc.input.redirecturl'
							)}
						/>
					</Grid>
					<Grid item xs="auto">
						<HelpTooltip
							title={intl.tr(`authMethods.fields.redirectURL.tooltip`)}
							placement="right"
						/>
					</Grid>
				</Grid>
				<ActionsFooter>
					<LoadingButton
						color="primary"
						variant="contained"
						onClick={() => formik.handleSubmit()}
						loading={formik.isSubmitting}
						disabled={!formik.dirty}
						sx={{ marginRight: 1 }}
					>
						{intl.tr('button.save')}
					</LoadingButton>
					<ApprovalDialogButton
						buttonText={intl.tr('button.cancel')}
						onApprove={formik.resetForm}
						disabled={!formik.dirty}
						title={intl.tr('discard.changes.dialog.title')}
						question={intl.tr('discard.changes.dialog.question')}
						approveText={intl.tr('discard.changes.dialog.approve')}
					/>
				</ActionsFooter>
			</Box>
		</Box>
	);
};

OIDCSettings.defaultProps = {
	settings: DEFAULT_FORM
};

export default OIDCSettings;
